import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
    FaSearch,
    FaUserPlus,
    FaEdit,
    FaTrash,
    FaFileExcel,
    FaUpload,
    FaExclamationTriangle,
    FaTimes,
    FaSave,
    FaChevronLeft,
    FaChevronRight,
    FaSync
} from "react-icons/fa";
import API_BASE_URL from "../../../../config/api";
import { useAuth } from "../../../../context/AuthContext";

function Faculty() {
    const { user } = useAuth();
    const [faculty, setFaculty] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showEditModal, setShowEditModal] = useState(false);
    const [domains, setDomains] = useState([]);
    const [thrustAreas, setThrustAreas] = useState([]);
    const [currentFaculty, setCurrentFaculty] = useState({
        facultyId: "",
        userId: "",
        name: "",
        facultyAbr: "",
        email: "",
        department: "",
        designation: "",
        roles: [],
        thrustArea: "",
        coordinatorFor: "",
        areaOfInterest: [],
    });
    const [uploadedFaculty, setUploadedFaculty] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState({ type: "", message: "" });

    const fetchThrustAreas = async (department) => {
        if (!department) {
            setThrustAreas([]);
            return;
        }

        const deptMap = {
            "Information Technology": "Artificial Intelligence and Data Science",
            "Artificial Intelligence and Data Science": "Information Technology",
            "Electronics and Telecommunication Engineering": "Computer and Communication Engineering",
            "Computer and Communication Engineering": "Electronics and Telecommunication Engineering",
            "Mechanical Engineering": "Robotics and Artificial Intelligence",
            "Robotics and Artificial Intelligence": "Mechanical Engineering"
        };

        let departmentsToFetch = [department];
        if (deptMap[department]) {
            departmentsToFetch.push(deptMap[department]);
        }

        try {
            // Use the main endpoint with query params which supports comma-separated departments
            const response = await axios.get(`${API_BASE_URL}/api/thrust-areas`,
                {
                    params: {
                        department: departmentsToFetch.join(',')
                    },
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );
            if (response.data && response.data.data) {
                setThrustAreas(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching thrust areas:", error);
            setThrustAreas([]);
        }
    };

    // Fetch domains on mount
    useEffect(() => {
        const fetchDomains = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/domains`,
                    {
                        headers: {
                            Authorization: `Bearer ${user.token}`,
                        },
                    }
                );
                if (response.data) {
                    setDomains(response.data);
                }
            } catch (error) {
                console.error("Error fetching domains:", error);
            }
        };

        fetchDomains();
    }, []);

    // Fetch thrust areas when department changes
    useEffect(() => {
        if (showEditModal && currentFaculty.department) {
            fetchThrustAreas(currentFaculty.department);
        } else {
            setThrustAreas([]);
        }
    }, [currentFaculty.department, showEditModal]);

    // Fetch faculty when page or search changes
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchFaculty();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [page, search]);

    const fetchFaculty = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/faculty`, {
                params: {
                    page: page,
                    limit: 9,
                    search: search
                },
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            });

            if (response.data && Array.isArray(response.data.faculty)) {
                setFaculty(response.data.faculty);
                setTotalPages(response.data.totalPages);
            } else {
                setFaculty([]);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching faculty data:", error);
            setIsLoading(false);
            setSaveStatus({
                type: "error",
                message: "Failed to load faculty: " + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleDelete = async (facultyId) => {
        try {
            if (!facultyId) return;
            if (!window.confirm("Are you sure you want to delete this faculty member?")) return;

            await axios.delete(
                `${API_BASE_URL}/api/faculty/${facultyId}`,
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            fetchFaculty();

            setSaveStatus({
                type: "success",
                message: "Faculty deleted successfully"
            });
            setTimeout(() => setSaveStatus({ type: "", message: "" }), 3000);
        } catch (error) {
            console.error("Error deleting faculty:", error);
            setSaveStatus({
                type: "error",
                message: "Failed to delete faculty: " + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleEdit = (facultyMember) => {
        setCurrentFaculty({ ...facultyMember });
        setShowEditModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentFaculty({ ...currentFaculty, [name]: value });
    };

    const handleRoleChange = (e) => {
        const role = e.target.value;
        const isChecked = e.target.checked;
        let updatedRoles = [...(currentFaculty.roles || [])];

        if (isChecked) {
            updatedRoles.push(role);
        } else {
            updatedRoles = updatedRoles.filter(r => r !== role);
        }

        setCurrentFaculty({ ...currentFaculty, roles: updatedRoles });
    };

    const handleMultiSelectChange = (e) => {
        const selectedOptions = Array.from(
            e.target.selectedOptions,
            (option) => option.value
        );
        setCurrentFaculty({
            ...currentFaculty,
            areaOfInterest: selectedOptions
        });
    };

    const handleSave = async () => {
        try {
            if (!currentFaculty.facultyId || !currentFaculty.userId ||
                !currentFaculty.email || !currentFaculty.name || !currentFaculty.department) {
                setSaveStatus({
                    type: "error",
                    message: "Please fill in all required fields"
                });
                return;
            }

            if (currentFaculty._id) {
                // Update existing faculty
                await axios.put(
                    `${API_BASE_URL}/api/faculty/${currentFaculty._id}`,
                    currentFaculty,
                    { headers: { Authorization: `Bearer ${user.token}` } }
                );

                setSaveStatus({
                    type: "success",
                    message: "Faculty updated successfully"
                });
            } else {
                // Create new faculty
                await axios.post(
                    `${API_BASE_URL}/api/faculty/single`,
                    currentFaculty,
                    { headers: { Authorization: `Bearer ${user.token}` } }
                );

                setSaveStatus({
                    type: "success",
                    message: "Faculty added successfully"
                });
            }

            setShowEditModal(false);
            fetchFaculty();
            setTimeout(() => setSaveStatus({ type: "", message: "" }), 3000);
        } catch (error) {
            console.error("Error saving faculty:", error);
            setSaveStatus({
                type: "error",
                message: "Failed to save faculty: " + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleAddFaculty = () => {
        const newFaculty = {
            facultyId: "",
            userId: "",
            email: "",
            name: "",
            facultyAbr: "",
            department: "",
            designation: "",
            roles: [],
            thrustArea: "",
            coordinatorFor: "",
            areaOfInterest: [],
        };
        setCurrentFaculty(newFaculty);
        setShowEditModal(true);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1); // Reset to page 1 on new search
    };

    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(sheet);

                    // Transformation logic to match schema
                    const processedData = jsonData.map(item => ({
                        facultyId: item["Sr. No"]?.toString() || item.facultyId || "",
                        userId: item["Sr. No"]?.toString() || item.userId || "",
                        name: item["Faculty Name"] || item.name || "",
                        facultyAbr: item["Faculy abr"] || item.facultyAbr || "",
                        email: item["email id"] || item.email || "",
                        department: item["Department"] || item.department || "",
                        designation: item["Designation"] || item.designation || "",
                        roles: item.roles ? (Array.isArray(item.roles) ? item.roles : String(item.roles).split(',').map(r => r.trim())) : [],
                        thrustArea: item.thrustArea || "",
                        areaOfInterest: item.areaOfInterest || []
                    }));

                    setUploadedFaculty(processedData);
                    setUploadError(null);
                } catch (error) {
                    setUploadError("Error processing file. Please check the format.");
                    console.error(error);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleSubmitFaculty = async () => {
        try {
            if (!uploadedFaculty) {
                setUploadError("Please upload a file first");
                return;
            }

            const response = await axios.post(
                `${API_BASE_URL}/api/faculty`,
                { faculty: uploadedFaculty },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            if (response.status === 201) {
                setSaveStatus({
                    type: "success",
                    message: "Faculty data uploaded successfully!"
                });

                setUploadedFaculty(null);
                setUploadError(null);

                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) {
                    fileInput.value = "";
                }

                fetchFaculty();

                setTimeout(() => setSaveStatus({ type: "", message: "" }), 3000);
            }
        } catch (error) {
            setUploadError(
                error.response?.data?.message || "Error uploading faculty data"
            );
            console.error("Error uploading faculty:", error);
        }
    };

    const rolesList = [
        { id: "guide", label: "Guide" },
        { id: "thrustarea coordinator", label: "Thrust Area Coord." },
        { id: "project coordinator", label: "Project Coord." },
        { id: "admin", label: "Admin" }
    ];

    return (
        <div className="min-h-screen p-6 md:p-12 font-sans">
            <div className="max-w-7xl mx-auto">
                <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">
                            Faculty Directory
                        </h1>
                        <p className="text-gray-500 mt-2">Manage faculty members and roles</p>
                    </div>
                </header>

                {/* Alerts */}
                {saveStatus.message && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 shadow-sm animate-fade-in ${saveStatus.type === "success"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                        }`}>
                        {saveStatus.type === "success" ? <FaSave /> : <FaExclamationTriangle />}
                        {saveStatus.message}
                    </div>
                )}

                {/* Actions Bar */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8 flex flex-col lg:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full lg:max-w-md">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search Faculty by Name, Dept..."
                            value={search}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] transition-all"
                        />
                    </div>

                    <div className="flex gap-3 w-full lg:w-auto">
                        <button
                            onClick={handleAddFaculty}
                            className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-[#b7202e] hover:bg-[#901a24] text-white px-5 py-2.5 rounded-lg transition-colors shadow-md shadow-red-100 font-medium"
                        >
                            <FaUserPlus /> Add Faculty
                        </button>
                        <button
                            onClick={() => fetchFaculty()}
                            className="flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-lg transition-colors"
                            title="Refresh List"
                        >
                            <FaSync className={isLoading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* Upload Section */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FaFileExcel className="text-green-600" /> Bulk Upload
                    </h3>
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                        <div className="flex-1 w-full">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <FaUpload className="w-8 h-8 mb-3 text-gray-400" />
                                    <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                    <p className="text-xs text-gray-500">XLSX, XLS or CSV</p>
                                </div>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileUpload}
                                />
                            </label>
                        </div>

                        {uploadedFaculty && (
                            <div className="w-full md:w-auto flex flex-col gap-3 animate-fade-in">
                                <div className="p-4 bg-green-50 rounded-lg border border-green-100 text-green-700 text-sm font-medium">
                                    {uploadedFaculty.length} records loaded and ready.
                                </div>
                                <button
                                    onClick={handleSubmitFaculty}
                                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors shadow-sm"
                                >
                                    Submit to Database
                                </button>
                            </div>
                        )}
                    </div>
                    {uploadError && (
                        <div className="mt-3 text-red-500 text-sm flex items-center gap-2">
                            <FaExclamationTriangle /> {uploadError}
                        </div>
                    )}
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {isLoading && !faculty.length ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#b7202e] rounded-full animate-spin mb-4"></div>
                            <p>Loading faculty records...</p>
                        </div>
                    ) : faculty.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-sm uppercase tracking-wider">
                                            <th className="p-4 font-semibold">Faculty ID</th>
                                            <th className="p-4 font-semibold">Name</th>
                                            <th className="p-4 font-semibold">Abr</th>
                                            <th className="p-4 font-semibold">Department</th>
                                            <th className="p-4 font-semibold w-1/3">Roles & Information</th>
                                            <th className="p-4 font-semibold text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {faculty.map((item, index) => (
                                            <tr key={item._id || index} className="hover:bg-red-50/30 transition-colors align-top">
                                                <td className="p-4 text-gray-700 font-medium whitespace-nowrap">{item.facultyId}</td>
                                                <td className="p-4 text-gray-800 font-semibold">{item.name}</td>
                                                <td className="p-4 text-gray-600">{item.facultyAbr}</td>
                                                <td className="p-4 text-gray-600">
                                                    <span className="inline-block px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600 mb-1">
                                                        {item.department}
                                                    </span>
                                                    <div className="text-xs text-gray-400">{item.designation}</div>
                                                    <div className="text-xs text-gray-400 mt-1">{item.email}</div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="space-y-4">
                                                        {/* Roles Section */}
                                                        <div>
                                                            <span className="text-xs font-bold text-gray-500 uppercase">Roles</span>
                                                            <div className="flex flex-wrap gap-2 mt-1">
                                                                {item.roles && item.roles.map((role, idx) => (
                                                                    <span key={idx} className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs border border-blue-100">
                                                                        {role}
                                                                    </span>
                                                                ))}
                                                                {(!item.roles || item.roles.length === 0) && (
                                                                    <span className="text-xs text-gray-400">No roles assigned</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Areas of Interest */}
                                                        {item.areaOfInterest && item.areaOfInterest.length > 0 && (
                                                            <div>
                                                                <span className="text-xs font-bold text-gray-500 uppercase">Areas of Interest</span>
                                                                <div className="flex flex-wrap gap-1 mt-1">
                                                                    {item.areaOfInterest.map((interest, idx) => (
                                                                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                                                            {interest}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Thrust Area */}
                                                        {item.thrustArea && (
                                                            <div>
                                                                <span className="text-xs font-bold text-gray-500 uppercase">Thrust Area</span>
                                                                <div className="text-xs text-gray-700 mt-1">
                                                                    {item.thrustArea}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex flex-col gap-2">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Edit Details"
                                                        >
                                                            <FaEdit className="mx-auto" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item._id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete Faculty"
                                                        >
                                                            <FaTrash className="mx-auto" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={page === 1 || isLoading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaChevronLeft className="w-3 h-3" />
                                    Previous
                                </button>
                                <span className="text-sm text-gray-600">
                                    Page <span className="font-semibold text-gray-900">{page}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    disabled={page === totalPages || isLoading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                    <FaChevronRight className="w-3 h-3" />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="p-12 text-center text-gray-500 bg-gray-50">
                            <FaSearch className="text-4xl mx-auto mb-3 text-gray-300" />
                            <p>No faculty found matching your search.</p>
                        </div>
                    )}
                </div>

                {/* Edit Modal */}
                {showEditModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                                <h3 className="text-xl font-bold text-gray-800">
                                    {currentFaculty._id ? "Edit Faculty" : "Add New Faculty"}
                                </h3>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Faculty ID <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="facultyId"
                                            value={currentFaculty.facultyId}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">User ID <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            name="userId"
                                            value={currentFaculty.userId}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e]"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Name <span className="text-red-500">*</span></label>
                                        <input type="text" name="name" value={currentFaculty.name} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]" required />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Faculty Abbreviation <span className="text-red-500">*</span></label>
                                        <input type="text" name="facultyAbr" value={currentFaculty.facultyAbr} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]" required />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Email <span className="text-red-500">*</span></label>
                                        <input type="email" name="email" value={currentFaculty.email} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e]" required />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Department <span className="text-red-500">*</span></label>
                                        <select name="department" value={currentFaculty.department} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e] bg-white" required>
                                            <option value="">Select Department</option>
                                            <option value="Computer Engineering">Computer Engineering</option>
                                            <option value="Information Technology">Information Technology</option>
                                            <option value="Electronics and Computer Engineering">Electronics and Computer Engineering</option>
                                            <option value="Electronics and Telecommunication Engineering">Electronics and Telecommunication Engineering</option>
                                            <option value="Mechanical Engineering">Mechanical Engineering</option>
                                            <option value="Computer and Communication Engineering">Computer and Communication Engineering</option>
                                            <option value="Artificial Intelligence and Data Science">Artificial Intelligence and Data Science</option>
                                            <option value="Robotics and Artificial Intelligence">Robotics and Artificial Intelligence</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Designation <span className="text-red-500">*</span></label>
                                        <select name="designation" value={currentFaculty.designation} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e] bg-white" required>
                                            <option value="">Select Designation</option>
                                            <option value="Professor">Professor</option>
                                            <option value="Associate Professor">Associate Professor</option>
                                            <option value="Assistant Professor">Assistant Professor</option>
                                            <option value="Lab Assistant">Lab Assistant</option>
                                        </select>
                                    </div>

                                    <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-100 pt-6 mt-2">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Roles</label>
                                            <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                {rolesList.map((role) => (
                                                    <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            value={role.id}
                                                            checked={currentFaculty.roles?.includes(role.id)}
                                                            onChange={handleRoleChange}
                                                            className="rounded text-[#b7202e] focus:ring-[#b7202e]"
                                                        />
                                                        <span className="text-sm text-gray-700">{role.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-gray-700">Areas of Interest</label>
                                            <select
                                                name="areaOfInterest"
                                                value={currentFaculty.areaOfInterest || []}
                                                onChange={handleMultiSelectChange}
                                                multiple
                                                className="w-full h-40 p-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e] text-sm"
                                            >
                                                {domains.map((domain, index) => (
                                                    <option key={index} value={domain.name || domain} className="p-1">{domain.name || domain}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-400">Hold Ctrl/Cmd to select multiple</p>
                                        </div>
                                    </div>

                                    {currentFaculty.roles?.includes("thrustarea coordinator") && (
                                        <div className="space-y-2 col-span-1 md:col-span-2">
                                            <label className="text-sm font-semibold text-gray-700">Thrust Area</label>
                                            <select
                                                name="thrustArea"
                                                value={currentFaculty.thrustArea ? currentFaculty.thrustArea.split(',').map(s => s.trim()) : []}
                                                onChange={(e) => {
                                                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                                    setCurrentFaculty({ ...currentFaculty, thrustArea: selectedOptions.join(', ') });
                                                }}
                                                multiple
                                                className="w-full h-32 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e] bg-white"
                                            >
                                                {thrustAreas.map((area, index) => (
                                                    <option key={index} value={area.name}>{area.name} ({area.department})</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Hold Ctrl/Cmd to select multiple. These represent all areas the faculty is involved in.
                                            </p>
                                        </div>
                                    )}

                                    {currentFaculty.roles?.includes("thrustarea coordinator") && (
                                        <div className="space-y-2 col-span-1 md:col-span-2">
                                            <label className="text-sm font-semibold text-gray-700">Coordinator For (Primary) <span className="text-red-500">*</span></label>
                                            <select
                                                name="coordinatorFor"
                                                value={currentFaculty.coordinatorFor}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#b7202e] bg-white"
                                            >
                                                <option value="">Select Primary Thrust Area</option>
                                                {/* Show options from the selected thrust areas (if avail) or all */}
                                                {(currentFaculty.thrustArea ? currentFaculty.thrustArea.split(',').map(s => s.trim()).filter(Boolean) : []).map((areaName, index) => (
                                                    <option key={index} value={areaName}>{areaName}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-gray-500">
                                                Select the specific Thrust Area this faculty member will coordinate.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-5 py-2.5 rounded-lg bg-[#b7202e] text-white hover:bg-[#901a24] transition-colors shadow-sm font-medium"
                                >
                                    {currentFaculty._id ? "Save Changes" : "Add Faculty"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Faculty;
